<template>
  <div class="flex flex-col gap-3 px-4 py-3 transition-colors hover:bg-gray-50 sm:flex-row sm:items-center sm:gap-4">
    <div class="min-w-0 flex-1">
      <span class="block truncate font-medium text-gray-900">{{ template.data.name }}</span>
      <p v-if="description" class="truncate text-sm text-muted-foreground">{{ description }}</p>

      <div v-if="template.type === 'email' || workflows.length" class="mt-1.5 flex flex-wrap items-center gap-2">
        <DeliveryModeEmailBadge v-if="template.type === 'email'" :delivery-mode="template.data.delivery_mode" />
        <Popover v-if="workflows.length">
          <PopoverTrigger as-child>
            <Badge as="button" type="button" variant="secondary" class="cursor-pointer hover:bg-secondary/80">
              <vue-feather type="git-merge" class="h-3 w-3" />
              {{ workflows.length }} {{ workflows.length === 1 ? 'workflow' : 'workflows' }}
            </Badge>
          </PopoverTrigger>
          <PopoverContent align="start" class="w-64">
            <p class="mb-2 text-xs font-medium uppercase tracking-wide text-muted-foreground">Used in</p>
            <ul class="flex flex-col gap-1">
              <li v-for="workflow in workflows" :key="workflow.id">
                <router-link
                  :to="{ name: 'WorkflowDetail', params: { id: workflow.id } }"
                  class="text-sm text-indigo-600 hover:underline"
                >
                  {{ workflow.name }}
                </router-link>
              </li>
            </ul>
          </PopoverContent>
        </Popover>
      </div>
    </div>

    <div class="flex shrink-0 items-center gap-1">
      <Tooltip v-if="template.type === 'email'">
        <TooltipTrigger as-child>
          <a :href="`/email_templates/${template.data.id}/preview`" target="_blank">
            <Button variant="ghost" size="icon" class="h-8 w-8" aria-label="Preview">
              <vue-feather type="eye" class="h-4 w-4" />
            </Button>
          </a>
        </TooltipTrigger>
        <TooltipContent>Preview</TooltipContent>
      </Tooltip>

      <Tooltip>
        <TooltipTrigger as-child>
          <Button variant="ghost" size="icon" class="h-8 w-8" aria-label="Edit" @click="emit('click-edit')">
            <vue-feather type="edit-2" class="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Edit</TooltipContent>
      </Tooltip>

      <Tooltip v-if="showDeleteButton()">
        <TooltipTrigger as-child>
          <span class="inline-flex">
            <Button
              variant="ghost"
              size="icon"
              class="h-8 w-8 text-muted-foreground hover:text-destructive"
              :disabled="deleteDisabled"
              aria-label="Delete"
              @click="emit('click-delete')"
            >
              <vue-feather type="trash-2" class="h-4 w-4" />
            </Button>
          </span>
        </TooltipTrigger>
        <TooltipContent>{{ deleteTooltip }}</TooltipContent>
      </Tooltip>
    </div>
  </div>
</template>

<script setup lang="ts">
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useTemplateRow, type TemplateEntityOption } from './useTemplateRow';
import DeliveryModeEmailBadge from '@/components/shared/EmailBadge/DeliveryModeEmailBadge.vue';

export type TemplateRowProps = {
  template: TemplateEntityOption;
};

const { template } = defineProps<TemplateRowProps>();
const { description, workflows, deleteDisabled, deleteTooltip, showDeleteButton } = useTemplateRow(template);

type Emits = {
  'click-delete': [];
  'click-edit': [];
};
const emit = defineEmits<Emits>();
</script>
